import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import dayjs from 'dayjs';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { ExpiredReminderDashboardDto } from 'api/dashboard/dtos/ExpiredReminderDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { DATE_FORMAT_DEFAUT } from 'Config';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetPeriodSelect, { PeriodOption } from '../components/WidgetPeriodSelect';
import KpiHeader from '../components/KpiHeader';
import useInfiniteReveal from '../components/useInfiniteReveal';
import upcomingStyles from '../upcomingEvents/UpcomingEvents.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ExpiredReminders.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const PERIOD_OPTIONS: PeriodOption[] = [
	{ value: '2', labelKey: 'dashboard.period_2_days' },
	{ value: '7', labelKey: 'dashboard.period_7_days' },
	{ value: '30', labelKey: 'dashboard.period_1_month' },
	{ value: '90', labelKey: 'dashboard.period_3_months' },
	{ value: 'custom', labelKey: 'dashboard.period_custom' },
];

type Props = {
	data?: ExpiredReminderDashboardDto[] | null;
	isLoading?: boolean;
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const ExpiredReminders = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<ExpiredReminderDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [period, setPeriod] = useState<string>('7');
	const { dateFrom: customFrom, dateTo: customTo, setDateRange } = useWidgetFilters('expiredReminders');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const isLegacyMode = props.data !== undefined;

	const getDateRange = () => {
		if (period === 'custom') {
			return { dateFrom: customFrom, dateTo: customTo };
		}
		const days = parseInt(period, 10);
		return {
			dateFrom: dayjs().subtract(days, 'day').startOf('day').toDate(),
			dateTo: dayjs().endOf('day').toDate(),
		};
	};

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const range = getDateRange();
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom: range.dateFrom, dateTo: range.dateTo, ...vehicleScope };
				const result = await DashboardService.getExpiredReminders(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, period, customFrom, customTo]);

	const data = isLegacyMode ? props.data ?? [] : fetchedData ?? [];
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const { visibleItems: pageItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const formatCriteria = (item: ExpiredReminderDashboardDto) => {
		if (item.criteria === 'DATE' && item.nextDate) {
			return dayjs(item.nextDate).format(DATE_FORMAT_DEFAUT);
		}
		if (item.criteria === 'KMS' && item.nextKm != null) {
			return `${item.nextKm.toLocaleString()} km`;
		}
		if (item.criteria === 'HOURS' && item.nextHour != null) {
			return `${item.nextHour.toLocaleString()} ${t('common.hours_symbol')}`;
		}
		return '-';
	};

	const buildVehicleTooltip = (item: ExpiredReminderDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	const formatCategory = (item: ExpiredReminderDashboardDto) => {
		if (item.vehicleMaintenanceTypeName) {
			return item.vehicleMaintenanceTypeIsSystem
				? t(('common.maintenance_contract_type.' + item.vehicleMaintenanceTypeName) as any) as string
				: item.vehicleMaintenanceTypeName;
		}
		return item.categoryIsSystem
			? t(`reminder_categories.category_types.${item.categoryDesignation}` as any) as string
			: item.categoryDesignation;
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.expired_reminders.title') as string}
			subTitleTooltip={t('dashboard.expired_reminders.tooltip' as any) as string}
			renderOptions={!isLegacyMode ? (
				<WidgetPeriodSelect
					value={period}
					options={PERIOD_OPTIONS}
					onChange={setPeriod}
					dateFrom={customFrom}
					dateTo={customTo}
					onDateRangeChange={setDateRange}
				/>
			) : undefined}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={String(data.length)}
						subLabel={t('dashboard.expired_reminders.total_expired' as any) as string}
						currentTotal={data.length}
						previousTotal={null}
						previousRange={null}
					/>
					<div className={upcomingStyles.tableWrapper}>
						<table className={upcomingStyles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.expired_reminders.col_asset')}</th>
									<th>{t('dashboard.expired_reminders.col_category')}</th>
									<th>{t('dashboard.expired_reminders.col_criteria')}</th>
									<th>{t('dashboard.expired_reminders.col_expired_at')}</th>
									<th>{t('dashboard.expired_reminders.col_email')}</th>
								</tr>
							</thead>
							<tbody>
								{pageItems.map(item => (
									<tr key={item.triggerId} className={styles.rowExpired}>
										<td>
											<span
												className={styles.plate}
												data-tip={buildVehicleTooltip(item)}
												data-for="expired-reminders-tooltip"
												data-place="right"
											>
												{item.registrationNumber}
											</span>
										</td>
										<td>{formatCategory(item)}</td>
										<td>{formatCriteria(item)}</td>
										<td>{item.expiredAt ? dayjs(item.expiredAt).format(DATE_FORMAT_DEFAUT) : '-'}</td>
										<td>{item.notificationEmail || '-'}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={5} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="expired-reminders-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default ExpiredReminders;
